import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Paper,
  Divider,
  Badge
} from '@mui/material';
import { Close, Send, AttachFile, Reply, Add } from '@mui/icons-material';
import { useProjectStore } from '../store/useProjectStore';
import { DiscussionMessage, DiscussionTopic } from '../types';
import { formatDate } from '../utils/helpers';

interface DiscussionModalProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
  subtaskId?: string;
  subtaskTitle?: string;
}

const DiscussionModal: React.FC<DiscussionModalProps> = ({
  open,
  onClose,
  taskId,
  taskTitle,
  subtaskId,
  subtaskTitle
}) => {
  const { 
    getDiscussion, 
    getDiscussionTopics, 
    addDiscussionMessage, 
    addDiscussionTopic,
    replyToMessage 
  } = useProjectStore();
  
  const [activeTab, setActiveTab] = useState('general');
  const [messageText, setMessageText] = useState('');
  const [replyingTo, setReplyingTo] = useState<{ topicId: string; messageId: string } | null>(null);
  const [replyText, setReplyText] = useState('');
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicIcon, setNewTopicIcon] = useState('📌');

  const discussion = getDiscussion(taskId, subtaskId);
  const topics = getDiscussionTopics(taskId, subtaskId);

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    
    addDiscussionMessage(taskId, subtaskId, activeTab, {
      author: '我',
      content: messageText,
      attachments: []
    });
    
    setMessageText('');
  };

  const handleSendReply = (topicId: string, messageId: string) => {
    if (!replyText.trim()) return;
    
    replyToMessage(taskId, subtaskId, topicId, messageId, {
      author: '我',
      content: replyText,
      attachments: []
    });
    
    setReplyText('');
    setReplyingTo(null);
  };

  const handleAddTopic = () => {
    if (!newTopicName.trim()) return;
    
    addDiscussionTopic(taskId, subtaskId, {
      name: newTopicName,
      icon: newTopicIcon
    });
    
    setNewTopicName('');
    setNewTopicIcon('📌');
    setShowAddTopic(false);
  };

  const renderMessage = (message: DiscussionMessage, topicId: string, level: number = 0) => (
    <Box key={message.id} sx={{ display: 'flex', gap: 2, mb: 3, ml: level * 6 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
        {message.author.charAt(0)}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {message.author}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(message.timestamp, 'yyyy/MM/dd HH:mm')}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
          {message.content}
        </Typography>
        {message.attachments && message.attachments.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {message.attachments.map((attachment) => (
              <Chip
                key={attachment.id}
                label={attachment.name}
                size="small"
                icon={<AttachFile />}
                clickable
                sx={{ bgcolor: 'grey.100' }}
              />
            ))}
          </Box>
        )}
        {level === 0 && (
          <Button
            size="small"
            startIcon={<Reply />}
            onClick={() => {
              setReplyingTo({ topicId, messageId: message.id });
              setReplyText('');
            }}
            sx={{ textTransform: 'none' }}
          >
            回覆
          </Button>
        )}
        
        {/* Reply input */}
        {replyingTo?.messageId === message.id && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="輸入回覆..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              multiline
              minRows={1}
              maxRows={3}
              autoFocus
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSendReply(topicId, message.id)}
            >
              送出
            </Button>
            <Button
              size="small"
              onClick={() => setReplyingTo(null)}
            >
              取消
            </Button>
          </Box>
        )}
        
        {/* Render replies */}
        {message.replies && message.replies.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {message.replies.map((reply) => renderMessage(reply, topicId, level + 1))}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h6">{subtaskTitle ? '子任務討論' : '任務討論'}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {taskTitle} {subtaskTitle && `> ${subtaskTitle}`}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Topic Tabs */}
      <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {topics.map((topic) => (
            <Tab
              key={topic.id}
              value={topic.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{topic.icon}</span>
                  <span>{topic.name}</span>
                  <Badge badgeContent={topic.messageCount} color="primary" />
                </Box>
              }
            />
          ))}
          <Tab
            value="add"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Add fontSize="small" />
                <span>新增主題</span>
              </Box>
            }
            onClick={(e) => {
              e.preventDefault();
              setShowAddTopic(true);
            }}
          />
        </Tabs>
      </Box>

      {/* Add Topic Form */}
      {showAddTopic && (
        <Paper sx={{ p: 2, m: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>新增討論主題</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="圖示"
              value={newTopicIcon}
              onChange={(e) => setNewTopicIcon(e.target.value)}
              sx={{ width: 80 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="主題名稱"
              value={newTopicName}
              onChange={(e) => setNewTopicName(e.target.value)}
            />
            <Button variant="contained" size="small" onClick={handleAddTopic}>
              新增
            </Button>
            <Button size="small" onClick={() => setShowAddTopic(false)}>
              取消
            </Button>
          </Box>
        </Paper>
      )}

      {/* Messages */}
      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        {discussion?.topics[activeTab]?.length === 0 || !discussion?.topics[activeTab] ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary'
          }}>
            <Typography variant="h1" sx={{ fontSize: '3rem', mb: 1 }}>💬</Typography>
            <Typography>尚無討論內容</Typography>
          </Box>
        ) : (
          <Box>
            {discussion.topics[activeTab].map((message) => renderMessage(message, activeTab))}
          </Box>
        )}
      </DialogContent>

      {/* Message Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="輸入訊息..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <IconButton>
            <AttachFile />
          </IconButton>
          <Button
            variant="contained"
            endIcon={<Send />}
            onClick={handleSendMessage}
          >
            發送
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DiscussionModal; 